import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { CalendarPlus } from "lucide-react";
import { EventForm } from "./EventForm";
import { EventList, EventShape } from "./EventList";

type Props = {
  scope: { contact_id?: string; organization_id?: string; opportunity_id?: string };
};

export const EventPanels: React.FC<Props> = ({ scope }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<EventShape[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const canMutate = user?.tenant_role !== "viewer";

  const refetch = useCallback(async () => {
    setError("");
    const params: Record<string, string> = { limit: "200" };
    if (scope.contact_id) params.contact_id = scope.contact_id;
    if (scope.organization_id) params.organization_id = scope.organization_id;
    if (scope.opportunity_id) params.opportunity_id = scope.opportunity_id;
    try {
      const r = await api.get("/crm/events", { params });
      setItems(r.data.items || []);
    } catch {
      setError("Could not load appointments.");
    } finally {
      setLoading(false);
    }
  }, [scope.contact_id, scope.organization_id, scope.opportunity_id]);

  useEffect(() => { refetch(); }, [refetch]);

  const upcoming = items.filter((e) => e.status === "scheduled");
  const history = items.filter((e) => e.status !== "scheduled");

  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4 md:p-5"
             data-testid="record-event-panels">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Appointments
        </h3>
        {canMutate ? (
          <Button size="sm" onClick={() => setAddOpen(true)}
            data-testid="record-event-add-btn">
            <CalendarPlus className="w-4 h-4 mr-1" /> Schedule
          </Button>
        ) : null}
      </div>
      <div className="mt-4 space-y-4">
        {loading ? (
          <div className="text-sm text-slate-500" role="status" aria-busy="true"
            data-testid="record-events-loading">Loading appointments…</div>
        ) : error ? (
          <div role="alert" className="text-sm text-red-600"
            data-testid="record-events-error">{error}</div>
        ) : (
          <>
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                Upcoming
              </div>
              <EventList events={upcoming} onRefetch={refetch}
                testId="record-upcoming-events" readOnly={!canMutate}
                emptyText="No upcoming appointments." />
            </div>
            <div>
              <button className="text-xs font-medium text-slate-500 uppercase tracking-wide"
                onClick={() => setShowHistory((v) => !v)}
                data-testid="record-events-history-toggle">
                {showHistory ? "Hide" : "Show"} completed/cancelled ({history.length})
              </button>
              {showHistory ? (
                <div className="mt-2">
                  <EventList events={history} onRefetch={refetch}
                    testId="record-history-events" readOnly
                    emptyText="No past appointments yet." />
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>

      {canMutate && user ? (
        <EventForm open={addOpen} onOpenChange={setAddOpen}
          onSuccess={() => refetch()}
          scope={scope}
          currentUserId={user.id} />
      ) : null}
    </section>
  );
};
